import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { fmtMoneda } from '../lib/moneda';
import type { AvisoDePresupuesto } from '../types/pricer-datec.types';

export type AccionDeDecision = 'approve' | 'reject' | 'adjust';

interface Props {
  /** `null` = cerrado. */
  accion: AccionDeDecision | null;
  codigo?: string;
  /** La vigencia pasó mientras esperaba firma (S4): se avisa, y se puede firmar igual. */
  vencida?: boolean;
  venceEl?: string | null;
  enviando?: boolean;
  /** El presupuesto del cliente contra el total (F6 T13, provisional 17); ausente si quien firma no lo ve. */
  presupuestoAviso?: AvisoDePresupuesto | null;
  /** El comentario ya recortado; `undefined` si no escribió nada (sólo posible al aprobar). */
  onConfirmar: (comentario: string | undefined) => void;
  /** Cancelar o cerrar: NO se registra nada. */
  onCancelar: () => void;
}

const TEXTOS: Record<
  AccionDeDecision,
  { titulo: string; boton: string; placeholder: string; obligatorio: boolean }
> = {
  approve: {
    titulo: 'Aprobar cotización',
    boton: 'Aprobar',
    placeholder: 'Comentario (opcional)',
    obligatorio: false,
  },
  reject: {
    titulo: 'Rechazar cotización',
    boton: 'Rechazar',
    placeholder: 'Motivo del rechazo (obligatorio)',
    obligatorio: true,
  },
  adjust: {
    titulo: 'Pedir ajuste',
    boton: 'Pedir ajuste',
    placeholder: 'Qué hay que ajustar (obligatorio)',
    obligatorio: true,
  },
};

function textoDePresupuesto(a: AvisoDePresupuesto): string {
  if (a.estado === 'NO_INFORMADO') return 'Presupuesto del cliente: no informado';
  const tope = fmtMoneda(a.presupuesto, 'USD', null);
  if (a.estado === 'DENTRO') return `Presupuesto del cliente: ${tope} — dentro del presupuesto`;
  return `Presupuesto del cliente: ${tope} — superado por ${fmtMoneda(a.excede, 'USD', null)}`;
}

/**
 * El cuadro de voto propio de la aplicación (reemplaza al `prompt()` del navegador): aprobar con comentario
 * opcional, rechazar y pedir ajuste con motivo obligatorio. Cancelar o cerrarlo no llama a nada.
 */
export function DecisionDeAprobacionDialog({
  accion,
  codigo,
  vencida,
  venceEl,
  enviando,
  presupuestoAviso,
  onConfirmar,
  onCancelar,
}: Props) {
  const [comentario, setComentario] = useState('');
  useEffect(() => {
    if (accion) setComentario('');
  }, [accion]);

  if (!accion) return null;
  const t = TEXTOS[accion];
  const recortado = comentario.trim();
  const falta = t.obligatorio && recortado === '';

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCancelar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.titulo}</DialogTitle>
          <DialogDescription>{codigo ? `Cotización ${codigo}` : ''}</DialogDescription>
        </DialogHeader>
        {accion === 'approve' && vencida && (
          <div
            role="status"
            className="rounded-md border border-alerta/50 bg-alerta/10 px-3 py-2 text-sm"
          >
            <strong>Esta cotización venció{venceEl ? ` el ${venceEl}` : ''}.</strong> Si la aprobás,
            vale de nuevo desde hoy.
          </div>
        )}
        {presupuestoAviso && (
          <p
            className={`text-sm ${presupuestoAviso.estado === 'SUPERADO' ? 'font-medium text-alerta' : 'text-muted-foreground'}`}
            data-testid="presupuesto-en-firma"
          >
            {textoDePresupuesto(presupuestoAviso)}
          </p>
        )}
        <textarea
          rows={4}
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder={t.placeholder}
          aria-label="Comentario"
          className="w-full rounded-md border bg-background p-2 text-sm"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onCancelar} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={() => onConfirmar(recortado === '' ? undefined : recortado)}
            disabled={falta || enviando}
          >
            {t.boton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
